import { Application, Container, Rectangle, Text, BlurFilter, RendererType, type TextStyleOptions } from 'pixi.js';
import { gameLogger } from '../../utils/GameLogger';
import type { ICamera, WorldLayerHandle } from './IRenderer';
import { makeWorldLayerHandle } from './worldLayerHandle';
import { Z_LAYERS } from './Z_LAYERS';
import { computeWorldTransform } from './computeWorldTransform';
import { AllocCounters } from '../diagnostics/AllocCounters';
import { RenderCountTracker } from '../diagnostics/RenderCountTracker';
import { FramePhaseBudget } from '../diagnostics/FramePhaseBudget';
import { shouldQueryWebGlDebugRendererInfo } from './gpuDiagnostics';
import { flushGraphicsRedraws } from './graphicsRedrawQueue';
import { bootSearchParams } from '../../config/boot/bootUrl';

/**
 * PixiOverlayHost — singleton lifecycle for a transparent Pixi.js
 * overlay layered above the Phaser canvas.
 *
 * During the H-B migration window, Pixi-rendered surfaces (starting
 * with BarLabels in Stage 1) live in this overlay so they can ride
 * over the existing Phaser-rendered world without forking the canvas.
 * The overlay's `worldContainer` is offset each frame by the active
 * Phaser camera's scroll, so world-space coordinates map correctly
 * across both substrates.
 *
 * Once H5 ships (camera + scene migration), the Pixi camera takes
 * over and this host's role narrows to "the place Pixi.Application
 * lives". Until then, treat the Phaser camera coupling as a
 * deliberate temporary bridge.
 */
class PixiOverlayHostImpl {
  private app: Application | null = null;
  private worldContainer: Container | null = null;
  /**
   * VFX container — child of worldContainer (so it inherits the camera
   * transform), but pinned to the top of worldContainer's render order
   * via sortableChildren + a very-high zIndex. Anything added to this
   * container renders ABOVE every other world-space child until it's
   * destroyed or removeChild'd. Use addWorldVfxChild()/createWorldVfxText
   * to put a node here.
   *
   * 2026-05-10: explicit VFX layer (DJ ask: VFX always top layer until
   * flushed/dumped/destroyed). Pre-fix, VFX child order depended on
   * insertion sequence — a regular world sprite added after a VFX could
   * occlude the VFX.
   */
  private vfxContainer: Container | null = null;
  /**
   * Glow layer (VFX-evolution arc, M1.S3) — a persistent child of
   * vfxContainer carrying ONE BlurFilter. Children added here are
   * additive-blended (set by PixiGraphics when style.glow) and the single
   * blur pass turns the additive layer into bloom — luminous fairy glow,
   * impact flares, auras — at bounded cost (one filter pass for ALL glow,
   * clipped to the screen region by Pixi, NOT per-primitive).
   */
  private glowContainer: Container | null = null;
  /**
   * UI container — screen-space, NOT camera-bound. For overlays that
   * sit at fixed screen coordinates regardless of camera scroll
   * (vignette borders, "YOU DIED" text, screen-flash rectangles,
   * minimap-fixed widgets, etc.). Stage 5r addition.
   */
  private uiContainer: Container | null = null;
  // Phase H5 — typed as ICamera (read-only camera surface). PhaserCamera
  // adapter wraps the Phaser camera; future PixiCamera could substitute.
  private cameraSource: ICamera | null = null;
  private readyPromise: Promise<void> | null = null;
  private hostElement: HTMLDivElement | null = null;
  /** Window-capture pointerdown forwarder for empty-world clicks (see initialize). */
  private worldClickForwarder: ((e: PointerEvent) => void) | null = null;
  private tickListeners = new Set<(deltaMs: number) => void>();
  private lastRenderMs = 0;
  private lastTickerMs = 0;
  private gpuBackend = 'unknown';
  private gpuRenderer = '';

  /**
   * Lazy init. Returns the same promise across concurrent callers.
   * Safe to call multiple times.
   */
  async ensureReady(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = this.initialize();
    return this.readyPromise;
  }

  private async initialize(): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('PixiOverlayHost requires a browser environment');
    }

    const host = document.createElement('div');
    host.id = 'pixi-overlay-host';
    host.style.position = 'absolute';
    host.style.top = '0';
    host.style.left = '0';
    host.style.width = '100%';
    host.style.height = '100%';
    host.style.pointerEvents = 'none';
    host.style.zIndex = '5';
    document.body.appendChild(host);
    this.hostElement = host;

    const app = new Application();
    // Allow forcing the Pixi backend for A/B testing: ?pixiGpu=webgpu|webgl|canvas.
    // Default tries WebGL, then WebGPU, then Canvas, so a blocked GPU does not
    // leave the game entirely black (Chrome with HWA disabled, etc.).
    const pixiGpu = this.readUrlParam('pixiGpu');
    const preferences: Array<'webgl' | 'webgpu' | 'canvas'> =
      pixiGpu === 'webgpu' || pixiGpu === 'webgl' || pixiGpu === 'canvas'
        ? [pixiGpu]
        : ['webgl', 'webgpu', 'canvas'];
    // ?res=0.5 (or any number) overrides the DPR cap for fill-rate testing.
    // Default keeps the existing 1.0 cap.
    const resParam = this.readUrlParam('res');
    const resolution = resParam !== undefined
      ? Math.min(window.devicePixelRatio || 1, Number(resParam) || 1)
      : Math.min(window.devicePixelRatio || 1, 1.0);
    try {
      await app.init({
        resizeTo: window,
        backgroundAlpha: 0,
        // Antialias uses multisampling and burns GPU fill-rate on a transparent
        // overlay; vector edges are acceptable without it.
        antialias: false,
        autoDensity: true,
        // Cap device-pixel-ratio to avoid GPU fill-rate death on high-DPI
        // displays. 1.0 is a further cut from 1.25 for the first_field freezes
        // where render time is fine but the GPU/compositor is stalling.
        resolution,
        preference: preferences,
      });
    } catch (e) {
      gameLogger.error('renderer', 'Pixi Application init failed; no renderer available', {
        error: (e as Error)?.message ?? String(e),
        preferences,
      });
      throw e;
    }

    host.appendChild(app.canvas);
    app.canvas.style.position = 'absolute';
    app.canvas.style.top = '0';
    app.canvas.style.left = '0';
    // The canvas MUST receive pointer events or Pixi's EventSystem never
    // sees a pointerdown and no sprite (mob/NPC click-to-target) can ever be
    // clicked — with 'none' every click fell through to the inert
    // #phaser-game-container (2026-06-11 live probe). The host div stays
    // 'none'; a child re-enabling via 'auto' still receives events. React UI
    // keeps priority by stacking (#root z-index 10 > host z-index 5), and the
    // window-level capture listeners (InputSystem focus-recovery, DOMInput)
    // fire regardless of which element is the event target.
    app.canvas.style.pointerEvents = 'auto';

    // World-click forwarding (2026-06-11, click-to-target break #4): #root's
    // whole UI stack paints ABOVE the canvas, so even with the canvas
    // accepting pointer events, an empty-world click hit-tests onto whichever
    // inert full-screen layout div is topmost (#world-passthrough wrapper /
    // #phaser-game-container / #root / body) — never the canvas. We can't
    // pointer-events:none our way up the tree (#root must stay interactive
    // for ALL UI), so instead: when a pointerdown's target is one of the
    // known inert passthrough layers, re-dispatch a clone onto the canvas so
    // Pixi's EventSystem hit-tests the sprites (mob/NPC click-to-target).
    // Real UI clicks (buttons/windows/panels) never match and are untouched.
    const PASSTHROUGH_IDS = new Set(['root', 'phaser-game-container', 'world-passthrough', 'pixi-overlay-host']);
    this.worldClickForwarder = (e: PointerEvent) => {
      if ((e as any).__h2kForwardedToPixi) return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const inert =
        t === document.body ||
        t === document.documentElement ||
        (!!t.id && PASSTHROUGH_IDS.has(t.id));
      if (!inert) return;
      const clone = new PointerEvent('pointerdown', e);
      (clone as any).__h2kForwardedToPixi = true;
      app.canvas.dispatchEvent(clone);
    };
    window.addEventListener('pointerdown', this.worldClickForwarder, true);

    // WebGL context-loss resilience. A lost GPU context (driver hiccup,
    // tab-backgrounding, memory pressure) otherwise freezes the client: the
    // ticker keeps rendering to a dead context, throwing every frame and
    // flooding the logs. Two-part fix:
    //   1. preventDefault() on 'webglcontextlost' — WITHOUT this the browser
    //      never fires 'webglcontextrestored', so a transient loss becomes
    //      permanent.
    //   2. Stop the ticker while the context is gone and restart on restore,
    //      so we don't render to a dead context in the meantime.
    // Capture phase so this runs BEFORE Pixi's own internal handler (which can
    // stopImmediatePropagation), guaranteeing we preventDefault (→ the browser
    // will fire 'restored') and stop the ticker even when Pixi swallows the
    // bubbling event.
    app.canvas.addEventListener('webglcontextlost', (e: Event) => {
      e.preventDefault();
      try { app.ticker.stop(); } catch { /* ticker already torn down */ }
      gameLogger.warn('renderer', '⚠️ WebGL context lost — render suspended, awaiting restore');
    }, { capture: true });
    app.canvas.addEventListener('webglcontextrestored', () => {
      try { app.ticker.start(); } catch { /* ticker already torn down */ }
      gameLogger.info('renderer', '✅ WebGL context restored — render resumed');
    }, { capture: true });

    // world is its own render group (lane AL, plan 21 §10): the camera transform moves
    // on the GPU, so a pan no longer recomputes and re-packs every world object per frame.
    const world = new Container({ isRenderGroup: true });
    const ui = new Container();
    // vfx (2026-05-10, DJ ask): child of world, so it follows the camera; zIndex pins it
    // on top of world's sorted children. Destroyed VFX nodes free the layer.
    const vfx = new Container();
    world.sortableChildren = true;
    vfx.zIndex = Z_LAYERS.VFX;
    world.addChild(vfx);
    // Glow layer (VFX-evolution M1.S3): persistent child of vfx carrying ONE
    // BlurFilter. Children added via addWorldGlowChild are additive-blended
    // (PixiGraphics sets that when style.glow) — additive + blur = bloom, at
    // a single screen-clipped filter pass for ALL glow. Pixi clips filter
    // render targets to the visible region, so cost scales with screen size,
    // not world extent.
    const glow = new Container();
    // Diagnostic toggle ?nobloom=1 disables the blur entirely to test whether
    // the GPU bloom pass is causing driver/compositor stalls on older GPUs.
    if (this.readUrlParam('nobloom') !== '1') {
      // Cheaper bloom: one blur pass with a smaller kernel. The global glow layer
      // multiplies with the number of glow children, so keep it light.
      glow.filters = [new BlurFilter({ strength: 3, quality: 1 })];
    }
    vfx.addChild(glow);
    // World layer below UI so screen-fixed overlays (vignette, death
    // screen) sit above world-space sprites/text.
    app.stage.addChild(world);
    app.stage.addChild(ui);

    // Diagnostic toggle ?maxfps=N caps the ticker.
    //
    // Pixi's ticker is uncapped by default, so it renders at the display's
    // refresh rate. A real 2026-07-25 `presentation-stall` on DJ's Chrome (RTX
    // 2080 Ti, 2521x1322, 14 atmosphere layers) measured ~170 fps and a render
    // phase owning 728 ms of every 1000 ms of wall clock, while the event loop
    // stayed healthy (lag 9 ms, zero long tasks) and frames stopped for 184 ms.
    // A 165 Hz display asks for 2.75x the frames of 60 Hz through the same
    // scene-FX stack, which is a plausible way to saturate the present path.
    //
    // This is a MEASUREMENT toggle, not a fix: capping here and re-running the
    // same session is the A/B that decides whether frame rate is the cause. No
    // default behaviour changes until that A/B says so.
    const maxFps = Number(this.readUrlParam('maxfps') ?? '');
    if (Number.isFinite(maxFps) && maxFps > 0) {
      app.ticker.maxFPS = maxFps;
    }

    // Time the actual Pixi renderer pass (the ticker plugin calls app.renderer.render(), not app.render(), so we wrap the renderer).
    let tickerStart = 0;
    const originalRender = (app.renderer.render.bind(app.renderer) as (a: unknown, b?: unknown) => void);
    app.renderer.render = (args: unknown, deprecated?: unknown) => {
      const t0 = performance.now();
      flushGraphicsRedraws(); // PixiGraphics dedupe: trim shorter redraws before anything is drawn
      originalRender(args, deprecated);
      this.lastRenderMs = performance.now() - t0;
      this.lastTickerMs = performance.now() - tickerStart;
      // Feed the measured-time budget. `FramePhase` has always declared 'render', but nothing
      // recorded it, so a freeze attribution could only ever report scene+tween. That blind spot
      // mattered: on 2026-07-25 every stall >=250ms came back with scene=1..16 ms/s — the
      // instrument saying "not me" for both phases it could see, while the largest bucket of
      // frame work went unmeasured.
      FramePhaseBudget.record('render', this.lastRenderMs);
    };

    app.ticker.add((time) => {
      tickerStart = performance.now();
      AllocCounters.inc('pixi.ticker.frame');
      if (this.cameraSource && this.worldContainer) {
        AllocCounters.inc('pixi.ticker.computeWorldTransform');
        // Scale the world container to fill the canvas while preserving the
        // camera's "intended frame" (camera.width × camera.height). This is
        // the fix for the 2026-05-12 "camera unlocked from intended frame"
        // regression: previously we multiplied by camera.zoom only, which
        // left the world rendered at 1:1 logical px against camera world
        // units regardless of canvas size — wider browser windows showed
        // more world than intended. Now the rendered frame letterboxes to
        // fit the canvas. See `computeWorldTransform.ts` for the math.
        const xform = computeWorldTransform(
          { width: app.screen.width, height: app.screen.height },
          this.cameraSource,
        );
        this.worldContainer.scale.set(xform.scale, xform.scale);
        this.worldContainer.x = xform.worldX;
        this.worldContainer.y = xform.worldY;
      }
      // Cosmetic per-frame callbacks (bob tweens, glow pulses, etc.).
      // Invoked AFTER camera transform so listeners can apply offsets
      // in world space without fighting the camera.
      // Clamp dt to avoid animation/physics spirals when the browser pauses
      // rAF and resumes with a large gap.
      const dt = Math.min(time.deltaMS, 100);
      AllocCounters.inc('pixi.ticker.listenersFired', this.tickListeners.size);
      for (const fn of this.tickListeners) fn(dt);

      // Roll the per-entity render-count buckets at end-of-frame so the
      // EventRateOverlay sees the just-completed frame's counts. Any
      // entity with count > 1 is a double-render — visible in the RENDERS
      // section of the debug tool.
      RenderCountTracker.snapshotAndReset();
    });

    this.app = app;
    this.worldContainer = world;
    this.vfxContainer = vfx;
    this.glowContainer = glow;
    this.uiContainer = ui;

    // 2026-05-10 diagnostic: expose on window for live debug probes.
    // Lets DiagnosticHub + ad-hoc Playwright probes inspect render state
    // without requiring SDK round-trips.
    (window as unknown as { __PIXI_OVERLAY_HOST__?: unknown }).__PIXI_OVERLAY_HOST__ = this;

    this.captureGpuInfo(app);

    gameLogger.info('renderer', 'PixiOverlayHost initialized', {
      resolution: app.renderer.resolution,
      width: app.canvas.width,
      height: app.canvas.height,
      gpuBackend: this.gpuBackend,
      gpuRenderer: this.gpuRenderer,
      pixiGpuPreference: preferences.join(','),
    });
  }

  /**
   * Bind a Phaser camera as the source-of-truth for world<->screen
   * transforms. Pass `{ scrollX, scrollY, zoom }` (live object —
   * the host reads it every frame). Call once after the scene's
   * camera is created.
   */
  bindCameraSource(source: ICamera): void {
    this.cameraSource = source;
  }

  unbindCameraSource(): void {
    this.cameraSource = null;
  }

  /**
   * The live rendering canvas (Pixi `app.canvas`), or null before
   * `ensureReady()` resolves. This is the REAL visible canvas that fills
   * the viewport — unlike `GameHost.canvas`, which is a hidden placeholder.
   * Editor screen→world conversion reads its `getBoundingClientRect()` to
   * map `clientX/Y` into canvas-logical pixels.
   */
  getCanvas(): HTMLCanvasElement | null {
    return this.app?.canvas ?? null;
  }

  /** The Pixi renderer (for generateTexture / texture baking). Null before
   *  ensureReady() resolves. Used by PixiBakeGraphics. */
  getRenderer(): { generateTexture(obj: unknown): unknown } | null {
    return this.app?.renderer ?? null;
  }

  /**
   * Extract the current visible world (stage clipped to the screen rect) as
   * a base64 JPEG data-URL via Pixi's ExtractSystem — `canvas.toDataURL()`
   * is NOT reliable on a WebGL canvas without `preserveDrawingBuffer`, so
   * this re-renders the stage into an extract target instead. Used by the
   * ability-cast capture diagnostics (?abilityCapture=1). Null before init
   * or on any extract failure (capture is best-effort, never throws into
   * gameplay).
   */
  async extractFrameBase64(opts?: {
    quality?: number;
    resolution?: number;
    /** Optional crop rect in stage/screen coords (focused captures). */
    frame?: { x: number; y: number; width: number; height: number };
  }): Promise<string | null> {
    const app = this.app;
    if (!app) return null;
    try {
      const f = opts?.frame;
      return await app.renderer.extract.base64({
        target: app.stage,
        format: 'jpg',
        quality: opts?.quality ?? 0.6,
        resolution: opts?.resolution ?? 0.5,
        frame: f
          ? new Rectangle(f.x, f.y, f.width, f.height)
          : new Rectangle(0, 0, app.screen.width, app.screen.height),
      });
    } catch (e) {
      gameLogger.warn('render', `extractFrameBase64 failed: ${(e as Error)?.message ?? e}`);
      return null;
    }
  }

  /**
   * Map a world-space point to stage/screen coords using the SAME
   * letterboxed transform the ticker applies to worldContainer each frame
   * (computeWorldTransform). Null before init or before a camera source is
   * registered. Consumers: focused ability captures (crop rects around the
   * caster/target).
   */
  worldToScreen(wx: number, wy: number): { x: number; y: number } | null {
    const app = this.app;
    if (!app || !this.cameraSource) return null;
    const xform = computeWorldTransform(
      { width: app.screen.width, height: app.screen.height },
      this.cameraSource,
    );
    return { x: xform.worldX + wx * xform.scale, y: xform.worldY + wy * xform.scale };
  }

  /**
   * Current camera view in WORLD coords — center + viewport size — from the
   * bound camera source. Null before a camera is bound. Used by the M3
   * atmosphere layer to scatter ambiance around what the player can see,
   * not around world origin.
   */
  getCameraView(): { centerX: number; centerY: number; width: number; height: number } | null {
    const cam = this.cameraSource;
    if (!cam) return null;
    return { centerX: cam.centerX, centerY: cam.centerY, width: cam.width, height: cam.height };
  }

  /** Logical screen size (`app.screen`), or null before init. */
  getScreenSize(): { width: number; height: number } | null {
    const app = this.app;
    return app ? { width: app.screen.width, height: app.screen.height } : null;
  }

  createWorldText(text: string, style: TextStyleOptions): Text {
    if (!this.worldContainer) {
      throw new Error('PixiOverlayHost.createWorldText called before ensureReady() resolved');
    }
    const t = new Text({ text, style });
    this.worldContainer.addChild(t);
    return t;
  }

  /**
   * Create a Text node attached to the screen-space UI container —
   * ignores camera scroll. Used for full-screen overlays (YOU DIED,
   * VICTORY) and any other text that should stay screen-fixed.
   */
  createUiText(text: string, style: TextStyleOptions): Text {
    if (!this.uiContainer) {
      throw new Error('PixiOverlayHost.createUiText called before ensureReady() resolved');
    }
    const t = new Text({ text, style });
    this.uiContainer.addChild(t);
    return t;
  }

  /**
   * Add a child container to the world layer. Coordinates inside are
   * world-space (camera-relative).
   */
  addWorldChild(child: Container): void {
    if (!this.worldContainer) {
      throw new Error('PixiOverlayHost.addWorldChild called before ensureReady() resolved');
    }
    this.worldContainer.addChild(child);
  }

  /**
   * Create a per-layer world container at `depth` (zIndex in worldContainer's
   * sortableChildren stack). Atmosphere mints a layer's content into the
   * returned handle so one setAlpha dims the whole layer (universal opacity,
   * Scene FX v3 M1). Throws before ensureReady() — callers guard + fall back to
   * the default worldContainer path.
   */
  createWorldLayerContainer(depth: number): WorldLayerHandle {
    if (!this.worldContainer) {
      throw new Error('PixiOverlayHost.createWorldLayerContainer called before ensureReady() resolved');
    }
    return makeWorldLayerHandle(this.worldContainer, new Container(), depth);
  }

  /**
   * Add a child to the VFX layer. World-space (inherits camera transform
   * from worldContainer), but pinned to render ABOVE every other world
   * child via the vfxContainer's zIndex=1000 + worldContainer.sortableChildren.
   *
   * Stays on top until the child is destroy()'d or removeChild'd. Use this
   * for impact bursts, ability cast effects, projectile trails, hit-spark
   * particles, level-up flashes — anything that should never be occluded
   * by sprites/terrain at the same world position.
   *
   * 2026-05-10 (DJ ask: VFX always our top layer).
   */
  addWorldVfxChild(child: Container): void {
    if (!this.vfxContainer) {
      throw new Error('PixiOverlayHost.addWorldVfxChild called before ensureReady() resolved');
    }
    this.vfxContainer.addChild(child);
  }

  /**
   * Add a child to the GLOW layer (VFX-evolution M1.S3). World-space
   * (inherits the camera transform via vfxContainer), additive + blurred =
   * bloom. Use for luminous fairy glow, impact flares, caster auras, light
   * halos — anything that should read as emitting light. PixiGraphics routes
   * here automatically when GraphicsStyle.glow is set.
   */
  addWorldGlowChild(child: Container): void {
    if (!this.glowContainer) {
      throw new Error('PixiOverlayHost.addWorldGlowChild called before ensureReady() resolved');
    }
    this.glowContainer.addChild(child);
  }

  /**
   * Create a Text node attached to the VFX layer (world-space, on-top).
   * Mirrors createWorldText but goes into vfxContainer.
   */
  createWorldVfxText(text: string, style: TextStyleOptions): Text {
    if (!this.vfxContainer) {
      throw new Error('PixiOverlayHost.createWorldVfxText called before ensureReady() resolved');
    }
    const t = new Text({ text, style });
    this.vfxContainer.addChild(t);
    return t;
  }

  /**
   * Flush all VFX. Removes (and destroys) every child of the VFX layer.
   * Use on scene change, level reload, or "clear all VFX" debug commands.
   *
   * Children's own destroy() runs via Pixi's removeChildren({ children: true })
   * pathway — anything holding a reference (animation tween, callback)
   * will see the node as destroyed (`.destroyed === true`).
   */
  flushVfx(): void {
    if (!this.vfxContainer) return;
    const removed = this.vfxContainer.removeChildren();
    for (const child of removed) {
      // The persistent glow layer (filter host) is preserved: clear its
      // children but keep + re-attach the container so the single BlurFilter
      // survives a flush.
      if (child === this.glowContainer) {
        const glowKids = (child as Container).removeChildren();
        for (const k of glowKids) {
          try { (k as { destroy?: (opts?: unknown) => void }).destroy?.({ children: true }); } catch { /* swallow */ }
        }
        this.vfxContainer.addChild(child as Container);
        continue;
      }
      try { (child as { destroy?: (opts?: unknown) => void }).destroy?.({ children: true }); } catch { /* swallow */ }
    }
  }

  /**
   * Add a child container to the UI layer. Coordinates inside are
   * SCREEN-SPACE — not transformed by the Phaser camera. Used for
   * vignette borders, "YOU DIED" overlays, and other screen-fixed UI
   * that should stay put while the camera scrolls.
   */
  addUiChild(child: Container): void {
    if (!this.uiContainer) {
      throw new Error('PixiOverlayHost.addUiChild called before ensureReady() resolved');
    }
    this.uiContainer.addChild(child);
  }

  /**
   * Register a per-frame callback (delta in ms). Returns an unsubscriber.
   * Used by surfaces that need cosmetic animation (bob, glow pulse, etc.)
   * before a full Tween primitive lands in H-B Stage 4.
   */
  addTickListener(fn: (deltaMs: number) => void): () => void {
    this.tickListeners.add(fn);
    return () => { this.tickListeners.delete(fn); };
  }

  /** Number of registered per-frame callbacks. */
  getTickListenerCount(): number {
    return this.tickListeners.size;
  }

  /** Renderer viewport + ticker state for freeze diagnostics. */
  getRendererStats(): {
    screenWidth: number;
    screenHeight: number;
    resolution: number;
    tickListenerCount: number;
    lastRenderMs: number;
    lastTickerMs: number;
    gpuBackend: string;
    gpuRenderer: string;
    glowChildCount: number;
    vfxChildCount: number;
    worldChildCount: number;
  } | null {
    const app = this.app;
    if (!app) return null;
    return {
      screenWidth: app.screen.width,
      screenHeight: app.screen.height,
      resolution: app.renderer.resolution,
      tickListenerCount: this.tickListeners.size,
      lastRenderMs: this.lastRenderMs,
      lastTickerMs: this.lastTickerMs,
      gpuBackend: this.gpuBackend,
      gpuRenderer: this.gpuRenderer,
      glowChildCount: this.glowContainer?.children.length ?? 0,
      vfxChildCount: this.vfxContainer?.children.length ?? 0,
      worldChildCount: this.worldContainer?.children.length ?? 0,
    };
  }

  /** Read a query param from the current URL (safe in node/ssr). */
  private readUrlParam(key: string): string | undefined {
    try {
      return bootSearchParams().get(key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  /** Capture GPU backend + adapter/renderer info for diagnostics. Called once after init. */
  private captureGpuInfo(app: Application): void {
    const renderer = app.renderer as any;
    if (renderer.type === RendererType.WEBGPU) {
      this.gpuBackend = 'webgpu';
      const info = renderer.gpu?.adapter?.info as GPUAdapterInfo | undefined;
      this.gpuRenderer = info
        ? [info.vendor, info.architecture, info.device, info.description].filter(Boolean).join(' ')
        : '';
    } else if (renderer.type === RendererType.WEBGL) {
      // Build a fuller GPU fingerprint so we can detect software/VM renderers
      // (SwiftShader, llvmpipe, ANGLE wrapping a virtual GPU, etc.).
      const gl = renderer.context?.gl as WebGLRenderingContext | WebGL2RenderingContext | undefined;
      if (gl) {
        // Firefox deprecated WEBGL_debug_renderer_info and directs callers to
        // the standard RENDERER parameter. Avoid touching the extension there:
        // getExtension() itself emits the warning even when the value is unused.
        const dbg = shouldQueryWebGlDebugRendererInfo(navigator.userAgent)
          ? gl.getExtension('WEBGL_debug_renderer_info')
          : null;
        const rawVendor = dbg ? (gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) as string) : '';
        const rawRenderer = dbg ? (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string) : '';
        const vendor = (gl.getParameter(gl.VENDOR) as string) ?? '';
        const rendererName = (gl.getParameter(gl.RENDERER) as string) ?? '';
        const version = (gl.getParameter(gl.VERSION) as string) ?? '';
        const glsl = (gl.getParameter(gl.SHADING_LANGUAGE_VERSION) as string) ?? '';
        const isWebGL2 = 'WebGL2RenderingContext' in globalThis && gl instanceof WebGL2RenderingContext;
        this.gpuBackend = isWebGL2 ? 'webgl2' : 'webgl1';
        this.gpuRenderer = [
          rawVendor && `unmasked-vendor:${rawVendor}`,
          rawRenderer && `unmasked-renderer:${rawRenderer}`,
          vendor && `vendor:${vendor}`,
          rendererName && `renderer:${rendererName}`,
          version && `version:${version}`,
          glsl && `glsl:${glsl}`,
        ].filter(Boolean).join('; ');
      }
    } else {
      this.gpuBackend = renderer.name ?? String(renderer.type ?? 'unknown');
    }
  }

  destroy(): void {
    if (this.worldClickForwarder) {
      window.removeEventListener('pointerdown', this.worldClickForwarder, true);
      this.worldClickForwarder = null;
    }
    if (this.app) {
      this.app.destroy(true, { children: true });
      this.app = null;
    }
    if (this.hostElement) {
      this.hostElement.remove();
      this.hostElement = null;
    }
    this.worldContainer = null;
    this.vfxContainer = null;
    this.glowContainer = null;
    this.uiContainer = null;
    this.cameraSource = null;
    this.readyPromise = null;
    this.tickListeners.clear();
  }
}

export const PixiOverlayHost = new PixiOverlayHostImpl();
